import { apiClient } from './apiClient';

/**
 * Subscription Service
 * Handles subscription and payment operations
 */

// Enhanced types based on documentation
export interface SubscriptionPlan {
  id: string;
  name: string;
  basePrice: number;        // Monthly base price in Naira
  billingInterval: number;  // Number of months
  billingCycle: string;     // 'monthly', 'quarterly', 'biannual', 'annual'
  discountPercentage: number; // Plan's default discount
  totalPrice: number;       // Final price after plan discount
  planCode: string;         // Paystack plan code
  features: string[];
}

export interface Subscription {
  id: string;
  user_id: string;
  paystack_plan_id: string; // Updated from plan_id
  plan?: string; // Now nullable with default 'Premium'
  status: 'pending' | 'active' | 'cancelled' | 'suspended' | 'expired' | 'past_due' | 'inactive';
  amount: number;
  currency: string;
  billing_cycle?: string; // Now nullable with default 'quarterly'
  billing_period?: 'quarterly' | 'biannual' | 'annual' | 'monthly'; // Now nullable with default 'quarterly'
  current_period_start: string; // Updated from start_date
  next_billing_date: string;
  cancelled_at?: string;
  current_period_end?: string; // Updated from ends_on
  auto_renew: boolean;
  payment_method_id?: string;
  discount_amount: number;
  metadata: {
    paystack_plan_id: string; // Updated from plan_id
    plan_name: string;
    billing_cycle: string;
    billing_months: number;
    user_discount_percentage: number;
    plan_discount_percentage: number;
    total_discount_percentage: number;
    firstPaymentAmount: number;
    recurringAmount: number;
    discount_applied: boolean;
    last_billing_date: string;
  };
  card_details?: {
    brand: string;
    last4: string;
    exp_month: string;
    exp_year: string;
    bank: string;
    reusable: boolean;
    authorization_code: string;
    country_code: string;
    signature: string;
  };
  created_at: string;
  updated_at: string;
  paystack_subscription_code?: string;
  paystack_customer_code?: string;
  cancel_at_period_end: boolean;
  daysUntilRenewal?: number;
  /** Partner subscriptions bought through CA. Each keeps its own expiry. */
  addons?: SubscriptionAddon[];
}

/** A partner and its tiers as `GET /subscriptions/partners` returns them. */
export interface ApiPartner {
  id: string;
  slug: string;
  name: string;
  handle?: string;
  description?: string;
  logo_url?: string;
  is_active: boolean;
  partner_tiers: {
    id: string;
    slug: string;
    label: string;
    months: number;
    price: number;
    is_active: boolean;
  }[];
}

/**
 * A partner subscription (e.g. Owambe Vibez) bought through CA alongside the
 * CA plan. Runs on its own clock, so `current_period_end` is independent of
 * the CA billing period. Price and duration are snapshotted at purchase, so a
 * later partner price change doesn't rewrite what was already paid.
 */
export interface SubscriptionAddon {
  id: string;
  partner_id: string;
  partner_name: string;
  partner_tier_id: string;
  status: 'pending' | 'active' | 'expired' | 'cancelled';
  amount: number;
  months: number;
  current_period_start?: string;
  current_period_end?: string;
  auto_renew: boolean;
}

export interface PaymentMethod {
  id: string;
  userId: string;
  authorizationCode: string;
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  bank: string;
  isDefault: boolean;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
}

// Payment initialization types
export interface PaymentInitRequest {
  planId: string;
  email: string;
  name: string;
  phone?: string;
  discountCode?: string;
  callbackUrl?: string;
  /**
   * Partner add-ons to bundle into this payment. Only the keys go up — the
   * backend resolves the price from its own partner tiers, so a client can't
   * set what a partner costs.
   */
  addons?: { partnerId: string; tierId: string }[];
}

export interface PaymentInitResponse {
  authorizationUrl: string;
  reference: string;
  accessCode: string;
  amount: number;          // First payment amount (with user discount if any)
  recurringAmount: number; // Recurring payment amount (plan discount only)
  planId: string;
}

// Discount code types
export interface DiscountCode {
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  description?: string;
  expiresAt?: string;
  usageLimit?: number;
  usageCount: number;
  applicablePlans: string[];
  isActive: boolean;
}

// Discount validation response
export interface DiscountValidationResponse {
  isValid: boolean;
  discount?: DiscountCode;
  message?: string;
  discountAmount?: number;
  finalAmount?: number;
  discountPercentage?: number;
  discountType?: 'percentage' | 'fixed';
}

export interface Invoice {
  id: string;
  subscriptionId: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed' | 'cancelled';
  date: string;
  dueDate?: string;
  invoiceNumber: string;
  description?: string;
  paymentMethod?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  user_id: string;
  subscription_id?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'success' | 'failed' | 'cancelled';
  type: 'subscription' | 'payment' | 'refund';
  reference: string;
  description?: string;
  payment_method?: string;
  paystack_reference?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export const subscriptionService = {
  /**
   * Get available subscription plans
   * GET /subscriptions/plans
   */
  getPlans: async () => {
    return await apiClient.get<SubscriptionPlan[]>('/subscriptions/plans');
  },

  /**
   * Initialize payment for subscription
   * POST /subscriptions/waitlist
   */
  initializePayment: async (paymentData: PaymentInitRequest) => {
    return await apiClient.post<PaymentInitResponse>('/subscriptions/waitlist', paymentData);
  },

  /**
   * Partner communities a creator can bundle at checkout
   * GET /subscriptions/partners
   */
  getPartners: async () => {
    return await apiClient.get<{ partners: ApiPartner[] }>('/subscriptions/partners');
  },

  /**
   * The current user's partner add-ons
   * GET /subscriptions/addons
   */
  getAddons: async () => {
    return await apiClient.get<{ addons: SubscriptionAddon[] }>('/subscriptions/addons');
  },

  /**
   * Buy partner add-ons on their own — adding a partner part-way through a CA
   * cycle, or renewing one that lapsed. Bundling at checkout goes through
   * `initializePayment` with an `addons` array instead.
   * POST /subscriptions/addons
   */
  createAddonPayment: async (
    addons: { partnerId: string; tierId: string }[],
    callbackUrl?: string,
  ) => {
    return await apiClient.post<PaymentInitResponse>('/subscriptions/addons', {
      addons,
      callbackUrl,
    });
  },

  /**
   * Renew one add-on at the tier it's already on
   * POST /subscriptions/addons/:id/renew
   */
  renewAddon: async (addonId: string, callbackUrl?: string) => {
    return await apiClient.post<PaymentInitResponse>(
      `/subscriptions/addons/${addonId}/renew`,
      { callbackUrl },
    );
  },

  /**
   * Stop an add-on renewing. It stays active until its paid period ends.
   * DELETE /subscriptions/addons/:id
   */
  cancelAddon: async (addonId: string) => {
    return await apiClient.delete<{ addon: SubscriptionAddon }>(
      `/subscriptions/addons/${addonId}`,
    );
  },

  /**
   * Verify payment after completion
   * GET /subscriptions/verify/:reference
   */
  verifyPayment: async (reference: string) => {
    return await apiClient.get(`/subscriptions/verify/${reference}`);
  },

  /**
   * Validate discount code
   * GET /subscriptions/discount/validate?code=${code}&planId=${planId}
   */
  validateDiscountCode: async (code: string, planId?: string) => {
    const url = planId 
      ? `/subscriptions/discount/validate?code=${code}&planId=${planId}`
      : `/subscriptions/discount/validate?code=${code}`;
    return await apiClient.get<DiscountValidationResponse>(url);
  },

  /**
   * Get current user's subscription
   * GET /subscriptions/me
   */
  getCurrentSubscription: async () => {
    return await apiClient.get<Subscription>('/subscriptions/me');
  },

  /**
   * Cancel subscription
   * POST /subscriptions/cancel
   */
  cancelSubscription: async () => {
    return await apiClient.post('/subscriptions/cancel');
  },

  /**
   * Reactivate subscription
   * POST /subscriptions/reactivate
   */
  reactivateSubscription: async () => {
    return await apiClient.post('/subscriptions/reactivate');
  },

  /**
   * Manually renew subscription
   * Initializes a new payment for the user's existing plan. The webhook
   * activates the renewed subscription once payment is completed.
   * POST /subscriptions/renew
   */
  renewSubscription: async (data?: { planId?: string; discountCode?: string; callbackUrl?: string }) => {
    return await apiClient.post<PaymentInitResponse>('/subscriptions/renew', data || {});
  },

  /**
   * Get payment methods
   * GET /payment-methods
   */
  getPaymentMethods: async () => {
    return await apiClient.get<PaymentMethod[]>('/payment-methods');
  },

  /**
   * Add payment method
   * POST /payment-methods
   */
  addPaymentMethod: async (data: {
    authorizationCode: string;  // From Paystack transaction
    cardType?: string;         // 'card' (default)
    last4: string;            // Last 4 digits
    expiryMonth?: number;     // Card expiry month
    expiryYear?: number;      // Card expiry year
    bank?: string;           // Issuing bank
    setAsDefault?: boolean;  // Set as default payment method
  }) => {
    return await apiClient.post<PaymentMethod>('/payment-methods', data);
  },

  /**
   * Set default payment method
   * PUT /payment-methods/:id/default
   */
  setDefaultPaymentMethod: async (paymentMethodId: string) => {
    return await apiClient.put(`/payment-methods/${paymentMethodId}/default`);
  },

  /**
   * Delete payment method
   * DELETE /payment-methods/:id
   */
  deletePaymentMethod: async (paymentMethodId: string) => {
    return await apiClient.delete(`/payment-methods/${paymentMethodId}`);
  },

  /**
   * Get invoices/payment history
   * This would typically be GET /invoices or similar
   * Adjust endpoint based on actual API
   */
  getInvoices: async () => {
    return await apiClient.get<Invoice[]>('/invoices');
  },

  /**
   * Get user transactions for payment history
   * GET /subscriptions/transactions
   */
  getTransactions: async () => {
    return await apiClient.get('/subscriptions/transactions');
  },

  /**
   * Send discount code emails to multiple members
   * POST /subscription/discount/send-email
   */
  sendDiscountEmails: async (emails: string[]) => {
    return await apiClient.post('/subscriptions/discount/send-email', { emails });
  },
};
